// Monotonic invalidation counters, one per RTK Query cache tag, bumped whenever a data-change
// notification invalidates that tag (see useDataChangeInvalidator).
//
// RTK Query refetches its own cached entries when a tag is invalidated, so almost every consumer
// needs nothing more. Custom dashboard view code is the exception: it fetches KPI data imperatively
// from inside the sandboxed view, outside the query cache, so an invalidation has no entry to
// refresh. Those consumers read the tag's counter instead and re-run their fetches when it moves —
// which is how a completed gold pipeline run, or a push to a team's connected repo, reaches a
// custom view.
//
// Module-level rather than a Redux slice so every app that uses the shared invalidator gets it with
// no store wiring, the same way SignalRService keeps its handler sets here.

const versionsByTag = new Map<string, number>();
const listeners = new Set<() => void>();

/** Bumps the counter for each tag and notifies subscribers. */
export function bumpDataChangeVersions(tags: readonly string[]): void {
  if (tags.length === 0) {
    return;
  }

  tags.forEach(tag => versionsByTag.set(tag, (versionsByTag.get(tag) ?? 0) + 1));
  listeners.forEach(listener => listener());
}

/** The number of invalidations seen for a tag in this session. Zero until the first one. */
export function getDataChangeVersion(tag: string): number {
  return versionsByTag.get(tag) ?? 0;
}

export function subscribeToDataChangeVersions(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
